import React, { FunctionComponent, memo, useCallback, useContext, useEffect, useRef, useState } from 'react'
import styles from './ObjectHeader.module.sass'
import Button from '../../../../controls/Button/Button'
import { projectDetailActions } from '../../../../../redux/pages/project-detail'
import { HasAllPermissions, HasAnyPermission } from '../../../../etc/HasPermission'
import { ID } from '../../../../../types/UtilityTypes'
import { PERMISSIONS } from '../../../../../constants/permissions'
import { useAllPermissions, useRootDispatch, useRootSelector } from '../../../../../utils/hooks'
import ObjectStatusPicker from '../../../../etc/ObjectStatusPicker/ObjectStatusPicker'
import { EMPTY_PROJECT_WORK_OBJECT, ProjectWorkObject } from '../../../../../responses/objects'
import { objectSelector } from '../../../../../utils/selectors'
import { objectActions } from '../../../../../redux/data/object/object-actions'
import ObjectTitle from '../../../../etc/ObjectTitle/ObjectTitle'
import UserPicker from '../../../../data-components/pickers/UserPicker/UserPicker'
import { apiRequests } from '../../../../../http/config'
import { fromQueryWithReset } from '../../../../../utils/rx-operators'
import DeleteIcon from 'files/delete.svg'
import { NumberParam, useQueryParam } from 'use-query-params'
import scrollIntoView from 'scroll-into-view-if-needed'
import ConditionLink from '../../../../etc/ConditionLink'
import { urlWithParams } from '../../../../../http/utils'
import { ROUTES } from '../../../../../http/routes'
import clsx from 'clsx'
import { useQueries } from '../../../../../utils/hooks/useQueries'
import { statusContext } from '../../../../../constants/contexts'
import { RequestStatus } from '../../../../../responses/response'
import Brief, { isBriefEmpty } from '../Brief/Brief'
import { galleryActions } from '../../../../../redux/global/gallery/gallery-actions'
import { FileData } from '../../../../../responses/files'
import BriefEditIcon from 'files/brief-edit.svg'
import { diffAndPrepare, getId } from '../../../../../utils/entity-utils'
import { Classes, Colors } from '../../../../../styles'
import { isDefinedRecord } from '../../../../../utils/utils'
import EditIcon from 'files/edit.svg'
import { Dialog } from '../../../../etc/Dialog/Dialog'
import OkCancel from '../../../../controls/OkCancel/OkCancel'
import { ObjectEditState } from '../../../../../redux/data/object/object-reducer'
import { useObservableState } from 'observable-hooks'
import { User } from '../../../../../responses/users'

interface Props {
    className?: string,
    objectId: ID,
    withLink?: boolean,
    active?: boolean,
    arrow?: 'none',
    onClick?: () => void,
    onArrowClick?: () => void,
    onTextClick?: () => void,
    markWidth?: number,
}

const ObjectHeader: FunctionComponent<Props> = props => {
    const dispatch = useRootDispatch()

    const [remove, setRemove] = useState(false)
    const object = useRootSelector(objectSelector(props.objectId)) || EMPTY_PROJECT_WORK_OBJECT
    const isEdit = useRootSelector(state => ObjectEditState.is.update(state.data.object.editState) && state.data.object.editState.object.id == props.objectId)
    const isRename = useRootSelector(state => ObjectEditState.is.rename(state.data.object.editState) && state.data.object.editState.object.id == props.objectId)
    const status = useContext(statusContext)

    const [queryObject, setQueryObject] = useQueryParam('object', NumberParam)
    const ref = useRef<HTMLDivElement>(null)

    const canChangeCurator = useAllPermissions([PERMISSIONS.OBJECT.CURATOR_CHANGE])

    const {isMobile} = useQueries()

    const emptyBrief = isBriefEmpty(object.files, object.description)

    useEffect(() => {
        if (emptyBrief) {
            setRemove(false)
        }
    }, [emptyBrief])

    useEffect(() => {
        let timeout: number
        const current = ref.current
        if (queryObject == props.objectId && status === RequestStatus.SUCCESS && current) {
            dispatch(projectDetailActions.openObject(props.objectId))
            // wait animation
            timeout = window.setTimeout(() => {
                scrollIntoView(current, {
                    block: 'start',
                    behavior: 'smooth'
                })
                setQueryObject(undefined)
            }, 100)
        }
        return () => clearTimeout(timeout)
    }, [dispatch, queryObject, setQueryObject, props.objectId, status])

    const preview = useCallback((current: FileData) => {
        dispatch(galleryActions.open({
            type: 'object',
            objectId: props.objectId,
            current,
        }))
    }, [dispatch, props.objectId])

    const [users, onUserSearch] = useObservableState<User[], {search: string, reset: boolean}>(fromQueryWithReset(
        query => apiRequests.users.list({
            query: {...query, can_curated: 1}
        })
    ), [])

    return <div ref={ref} className={clsx(props.className, styles.container)}>
        <div className={styles.header}>
            <ConditionLink
                className={styles.link}
                condition={!!props.withLink}
                to={urlWithParams({
                    projectId: object.project_id,
                    objectId: object.id,
                })(ROUTES.OBJECT)}
            >
                {
                    props.withLink ? <ObjectTitle
                        className={styles.title}
                        active={props.active}
                        object={object}
                        arrow={'back'}
                        onClick={props.onClick}
                        onTextClick={props.onTextClick}
                        onArrowClick={props.onArrowClick}
                        markWidth={props.markWidth}
                    />: <ObjectTitle
                        className={styles.title}
                        active={props.active}
                        object={object}
                        onClick={props.onClick}
                        onTextClick={props.onTextClick}
                        onArrowClick={props.onArrowClick}
                        markWidth={props.markWidth}
                        editable={!props.withLink}
                        edit={isRename}
                        onEdit={() => dispatch(objectActions.startRename(object))}
                        onCancel={() => dispatch(objectActions.cancelEdit())}
                        onDuplicate={() => dispatch(objectActions.startDuplicate(object))}
                        onChange={name => dispatch(objectActions.rename({object, name}))}
                        button={true}
                    />
                }
            </ConditionLink>
            <UserPicker
                minimal
                onlyIcon={isMobile}
                data={users}
                className={styles.curator}
                text={'Добавить куратора'}
                title={'Изменить куратора'}
                user={object.curator}
                disabled={!canChangeCurator}
                onChange={(search, reset) => onUserSearch({search, reset})}
                onClick={curator => dispatch(objectActions.update.request({
                    objectId: props.objectId,
                    changes: {curator: curator.id}
                }))}
            />
            {
                !isMobile && <>
                    {
                        !props.withLink && <ObjectStatusPicker className={styles.status} object={object}/>
                    }
                    <div className={styles.actions}>
                        <HasAnyPermission
                            condition={!isMobile && !props.withLink}
                            permissions={[PERMISSIONS.PROJECT.EDIT, PERMISSIONS.OBJECT.DELETE]}
                        >
                            <Button
                                title={'Удалить объект'}
                                onClick={() => dispatch(objectActions.startDelete(object))}
                            >
                                <DeleteIcon/>
                            </Button>
                        </HasAnyPermission>
                        <HasAllPermissions condition={emptyBrief && !isEdit} permissions={[PERMISSIONS.PROJECT.EDIT]}>
                            <Button
                                title={'Добавить бриф'}
                                onClick={() => dispatch(objectActions.startEdit(object))}
                            >
                                <BriefEditIcon/>
                            </Button>
                        </HasAllPermissions>
                    </div>
                </>
            }
        </div>
        <Brief
            className={styles.brief}
            headerClassName={Classes.f13b}
            description={object.description}
            files={object.files}
            onFileClick={preview}
            text={'Бриф объекта'}
            edit={isEdit}
            onCancel={() => dispatch(objectActions.cancelEdit())}
            onChange={data => {
                const changes = diffAndPrepare<ProjectWorkObject>(object, {
                    description: data.text,
                    files: data.files
                })
                if (isDefinedRecord(changes)) {
                    dispatch(objectActions.update.request({
                        objectId: props.objectId,
                        changes,
                    }))
                } else {
                    dispatch(objectActions.cancelEdit())
                }
            }}
        >
            {
                !emptyBrief && <HasAllPermissions permissions={[PERMISSIONS.PROJECT.EDIT]}>
                    {
                        !isEdit ? <Button
                                title={'Редактировать бриф'}
                                onClick={event => {
                                    event.stopPropagation()
                                    dispatch(objectActions.startEdit(object))
                                }}
                            >
                                <EditIcon/>
                            </Button> :
                            <div/>
                    }
                    <Button
                        title={'Удалить бриф'}
                        onClick={event => {
                            event.stopPropagation()
                            setRemove(true)
                        }}
                    >
                        <DeleteIcon/>
                    </Button>
                </HasAllPermissions>
            }
        </Brief>
        {
            remove && <Dialog
                header={'Подтвердите удаление брифа'}
                onClose={() => setRemove(false)}
                actions={({onClose}) => <OkCancel
                    color={Colors.red}
                    onOk={() => dispatch(objectActions.update.request({
                        objectId: props.objectId,
                        changes: {
                            description: null,
                            files: {
                                delete: object.files.map(getId),
                            }
                        }
                    }))}
                    onCancel={onClose}
                />}
            >

            </Dialog>
        }
    </div>
}

export default memo(ObjectHeader)